import { useEffect, useState } from "react";
import { ImagePlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";
import { authApi } from "../../lib/auth";
import { errMsg } from "../../lib/api";

const CATS = ["Desk accessories", "Gaming gear", "Toys", "Personalised gifts", "Christmas"];
const blank = { name: "", category: "Toys", price: "", courier_fee: "", image: "", colors: "", description: "", material: "PLA", badge: "Ready to ship" };
const inputCls = "w-full rounded-2xl border-2 border-[var(--ink)] bg-white px-4 py-2.5 text-base outline-none focus:shadow-[4px_4px_0_var(--pink)] transition-shadow";

const toForm = (p) => (p ? { ...blank, ...p, price: String(p.price ?? ""), courier_fee: p.courier_fee == null ? "" : String(p.courier_fee), colors: (p.colors || []).join(", "), badge: p.badge ?? "" } : blank);

const L = ({ label, hint, children }) => (
  <label className="block">
    <span className="block font-semibold text-sm mb-1">{label}</span>
    {children}
    {hint && <span className="block text-xs text-[#5a5470] mt-1">{hint}</span>}
  </label>
);

export const ProductForm = ({ product, open, onClose, onSaved }) => {
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  useEffect(() => {
    if (open) setF(toForm(product));
  }, [open, product]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await authApi.post("/admin/upload", fd);
      setF((x) => ({ ...x, image: r.data?.url ?? "" }));
    } catch (e2) {
      toast.error(errMsg(e2));
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!f.image) return toast.error("Please add a photo");
    setBusy(true);
    const body = {
      ...f, price: Number(f.price), courier_fee: f.courier_fee === "" ? null : Number(f.courier_fee),
      colors: f.colors.split(",").map((c) => c.trim()).filter(Boolean),
    };
    try {
      if (product) await authApi.put(`/admin/products/${product.slug}`, body);
      else await authApi.post("/admin/products", body);
      toast.success(product ? "Item updated" : "Item added to the shop");
      onSaved();
      onClose();
    } catch (e2) {
      toast.error(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="product-form-modal" data-lenis-prevent className="max-w-2xl w-[94vw] max-h-[90vh] overflow-y-auto rounded-[28px] border-[2.5px] border-[var(--ink)] bg-[var(--paper)]">
        <DialogTitle className="font-display font-extrabold text-3xl">{product ? "Edit item" : "Add a new item"}</DialogTitle>
        <DialogDescription className="text-[#3b3550]">Changes show in the shop straight away.</DialogDescription>
        <form onSubmit={save} className="grid sm:grid-cols-2 gap-4 mt-2">
          <div className="sm:col-span-2 flex items-center gap-4">
            <div className="w-28 h-28 rounded-2xl border-2 border-[var(--ink)] bg-white overflow-hidden grid place-items-center shrink-0">
              {f.image ? <img src={f.image} alt="Item" className="w-full h-full object-cover" /> : <ImagePlus className="w-8 h-8 text-[#5a5470]" />}
            </div>
            <label className="press chunk-sm rounded-full bg-[var(--yellow)] px-5 py-2.5 font-bold cursor-pointer flex items-center gap-2">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
              {uploading ? "Uploading..." : f.image ? "Change photo" : "Upload photo"}
              <input type="file" accept="image/*" className="hidden" data-testid="product-form-photo-input" onChange={upload} />
            </label>
          </div>
          <div className="sm:col-span-2"><L label="Name *"><input required data-testid="product-form-name" className={inputCls} value={f.name} onChange={set("name")} /></L></div>
          <L label="Category *">
            <select data-testid="product-form-category" className={inputCls} value={f.category} onChange={set("category")}>
              {CATS.map((c) => <option key={c}>{c}</option>)}
            </select>
          </L>
          <L label="Price (NZD) *"><input required type="number" min="0.01" step="0.01" data-testid="product-form-price" className={inputCls} value={f.price} onChange={set("price")} /></L>
          <div className="sm:col-span-2">
            <L label="Courier price for this item (NZD)" hint="Leave blank to use the standard $12.50. Set a higher price for larger items.">
              <input type="number" min="0" step="0.01" data-testid="product-form-courier" className={inputCls} value={f.courier_fee} onChange={set("courier_fee")} placeholder="12.50" />
            </L>
          </div>
          <div className="sm:col-span-2"><L label="Colours" hint="Separate with commas, e.g. Red, Purple, Black"><input data-testid="product-form-colors" className={inputCls} value={f.colors} onChange={set("colors")} /></L></div>
          <div className="sm:col-span-2"><L label="Description"><textarea rows={3} data-testid="product-form-description" className={inputCls} value={f.description} onChange={set("description")} /></L></div>
          <L label="Material"><input data-testid="product-form-material" className={inputCls} value={f.material} onChange={set("material")} /></L>
          <L label="Label on photo" hint="e.g. Ready to ship, New, Sold out"><input data-testid="product-form-badge" className={inputCls} value={f.badge} onChange={set("badge")} /></L>
          <button type="submit" disabled={busy || uploading} data-testid="product-form-save" className="sm:col-span-2 press chunk rounded-full bg-[var(--pink)] text-white font-bold py-3.5 text-lg disabled:opacity-60">
            {busy ? "Saving..." : product ? "Save changes" : "Add to shop"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
